import '@/assets/css/main.css';
import SocialLogin from '@/components/auth/social-login';
import RateLimitContainer from '@/components/error/rate-limit-container';
import Gtm from '@/components/gtm/gtm';
import Maintenance from '@/components/maintenance/layout';
import MetaPixel from '@/components/meta-pixel/meta-pixel';
import DefaultSeo from '@/components/seo/default-seo';
import ChatWidget from '@/components/ui/chat-widget';
import ManagedDrawer from '@/components/ui/drawer/managed-drawer';
import ManagedModal from '@/components/ui/modal/managed-modal';
import { ModalProvider } from '@/components/ui/modal/modal.context';
import { SearchProvider } from '@/components/ui/search/search.context';
import { NotificationProvider } from '@/context/notify-content';
import { RateLimitProvider } from '@/context/rate-limit-context';
import QueryProvider from '@/framework/client/query-provider';
import { getDirection } from '@/lib/constants';
import PrivateRoute from '@/lib/private-route';
import { CartProvider } from '@/store/quick-cart/cart.context';
import { NextPageWithLayout } from '@/types';
import { SessionProvider } from 'next-auth/react';
import { appWithTranslation } from 'next-i18next';
import type { AppProps } from 'next/app';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import 'react-toastify/dist/ReactToastify.css';
const ToastContainer = dynamic(
  () => import('react-toastify').then((module) => module.ToastContainer),
  { ssr: false },
);

type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

function CustomApp({
  Component,
  pageProps: {
    //@ts-ignore
    session,
    ...pageProps
  },
}: AppPropsWithLayout) {
  const getLayout = Component.getLayout ?? ((page) => page);
  const authenticationRequired = Component.authenticationRequired ?? false;
  const { locale } = useRouter();
  const dir = getDirection(locale);
  const gtmId = process.env.NEXT_PUBLIC_GTM_ID ?? null;
  const metaPixelId = process.env.NEXT_PUBLIC_META_PIXEL_ID ?? null;

  if (!gtmId && process.env.NODE_ENV === 'production') {
    console.warn('Google Tag Manager ID is missing. Tracking is disabled.');
  }

  if (!metaPixelId && process.env.NODE_ENV === 'production') {
    console.warn('Meta Pixel ID is missing. Tracking is disabled.');
  }

  return (
    <>
      {metaPixelId && <MetaPixel pixelId={metaPixelId} />}
      {gtmId && <Gtm gtmId={gtmId} />}
      <div dir={dir}>
        <SessionProvider session={session}>
          <QueryProvider pageProps={pageProps}>
            <SearchProvider>
              <ModalProvider>
                <RateLimitProvider>
                  <CartProvider>
                    <>
                      <DefaultSeo />
                      <Maintenance>
                        <NotificationProvider>
                          {authenticationRequired ? (
                            <PrivateRoute>
                              {getLayout(<Component {...pageProps} />)}
                            </PrivateRoute>
                          ) : (
                            getLayout(<Component {...pageProps} />)
                          )}
                        </NotificationProvider>
                      </Maintenance>
                      <ManagedModal />
                      <ManagedDrawer />
                      <RateLimitContainer />
                      <ToastContainer autoClose={2000} theme="colored" />
                      <SocialLogin />
                      {process.env.NEXT_PUBLIC_CHAT_APP_ID && (
                        <ChatWidget
                          appId={process.env.NEXT_PUBLIC_CHAT_APP_ID}
                        />
                      )}
                    </>
                  </CartProvider>
                </RateLimitProvider>
              </ModalProvider>
            </SearchProvider>
          </QueryProvider>
        </SessionProvider>
      </div>
    </>
  );
}

export default appWithTranslation(CustomApp);
